function parseCollectionHTML(source) {
  return source && source.nodeType === 9
    ? source
    : new DOMParser().parseFromString(source, 'text/html');
}

function appendCollectionCards(source, destination, { animate = true } = {}) {
  const items = Array.from(source.querySelectorAll('.card'));
  const fragment = document.createDocumentFragment();
  const shouldAnimate = animate && !theme.config.motionReduced;
  const distance = theme.config.mqlSmall || theme.config.isTouch ? 18 : 24;

  items.forEach((item) => {
    // Set the first frame before the fragment enters the live DOM so cards never
    // flash at full opacity and then jump back to the reveal position.
    if (shouldAnimate) {
      item.style.opacity = '0.82';
      item.style.visibility = 'visible';
      item.style.transform = `translateY(${distance}px)`;
    }

    fragment.appendChild(item);
  });

  destination.appendChild(fragment);
  return items;
}

const collectionGridHistory = (() => {
  const storagePrefix = 'willow:collection-grid:';
  const maxAge = 30 * 60 * 1000;
  const navigation = performance.getEntriesByType?.('navigation')?.[0];
  const isHistoryNavigation = navigation?.type === 'back_forward'
    || performance.navigation?.type === 2;

  const storageKey = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    const sortedParams = new URLSearchParams(
      [...url.searchParams.entries()].sort(([keyA, valueA], [keyB, valueB]) => {
        return keyA.localeCompare(keyB) || valueA.localeCompare(valueB);
      })
    );
    url.search = sortedParams.toString();

    return `${storagePrefix}${url.pathname}${url.search}`;
  };

  const read = () => {
    if (!window.theme?.config?.hasSessionStorage) return null;

    try {
      const state = JSON.parse(sessionStorage.getItem(storageKey()));
      if (!state || Date.now() - state.updatedAt > maxAge) return null;
      return state;
    }
    catch (error) {
      return null;
    }
  };

  const write = (state) => {
    if (!window.theme?.config?.hasSessionStorage) return;

    try {
      sessionStorage.setItem(storageKey(), JSON.stringify({
        pages: Array.isArray(state.pages) ? state.pages : [],
        scrollY: Number(state.scrollY) || 0,
        updatedAt: Date.now()
      }));
    }
    catch (error) {
      // Storage can be unavailable in private browsing or the theme editor.
    }
  };

  const clear = () => {
    if (!window.theme?.config?.hasSessionStorage) return;

    try {
      sessionStorage.removeItem(storageKey());
    }
    catch (error) {
      // Do nothing when storage is unavailable.
    }
  };

  if (!isHistoryNavigation) clear();

  const savedState = isHistoryNavigation ? read() : null;
  const canRestore = Boolean(savedState?.pages?.length);
  const api = {
    restoring: canRestore,

    rememberScroll() {
      const state = read() || { pages: [] };
      state.scrollY = window.scrollY;
      write(state);
    },

    recordPage(url) {
      const state = read() || { pages: [], scrollY: window.scrollY };
      if (!state.pages.includes(url)) state.pages.push(url);
      state.scrollY = window.scrollY;
      write(state);
    },

    async restore() {
      if (!this.restoring) return;

      const productGridContainer = document.getElementById('ProductGridContainer');
      const destination = productGridContainer?.querySelector('motion-list');
      if (!productGridContainer || !destination) {
        this.restoring = false;
        return;
      }

      document.documentElement.setAttribute('data-collection-restoring', '');
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

      let lastPage = null;

      try {
        for (const url of savedState.pages) {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Collection restore failed: ${response.status}`);

          const parsedHTML = parseCollectionHTML(await response.text());
          const source = parsedHTML.querySelector('motion-list');
          if (source) appendCollectionCards(source, destination, { animate: false });
          lastPage = parsedHTML;
        }

        if (lastPage) {
          const currentPagination = productGridContainer.querySelector('.pagination');
          const restoredPagination = lastPage.querySelector('.pagination');

          if (currentPagination && restoredPagination) {
            currentPagination.innerHTML = restoredPagination.innerHTML;
          }
          else if (currentPagination) {
            currentPagination.remove();
          }

          document.dispatchEvent(new CustomEvent('collection:reloaded', {
            detail: { mode: 'append', restored: true }
          }));
        }

        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        window.scrollTo({ top: savedState.scrollY || 0, behavior: 'auto' });
      }
      catch (error) {
        console.error(error);
      }
      finally {
        this.restoring = false;
        document.documentElement.removeAttribute('data-collection-restoring');
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
        const infiniteButton = document.querySelector('button[is="infinite-button"]');
        infiniteButton?.startAutoLoad?.();
      }
    }
  };

  return api;
})();

/* Compact long facet lists (primarily Design Collection) without removing any
   choices from the form. Event delegation survives Shopify's facet AJAX swaps. */
(() => {
  if (window.__wvFacetValueToggle) return;
  window.__wvFacetValueToggle = true;

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('[data-facet-values-toggle]');
    if (!toggle) return;

    event.preventDefault();
    const details = toggle.closest('[data-filter]');
    if (!details) return;

    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    details.querySelectorAll('[data-facet-overflow]').forEach((item) => {
      item.hidden = expanded === false ? false : true;
    });

    toggle.setAttribute('aria-expanded', String(!expanded));
    const label = toggle.querySelector('[data-facet-toggle-label]');
    if (label) label.textContent = expanded ? 'View all' : 'Show less';
  });
})();

/* Keep finish names visible on touch devices and after Shopify replaces facet
   markup. Tooltips remain a secondary desktop affordance. */
(() => {
  if (window.__wvFacetSwatchStatus) return;
  window.__wvFacetSwatchStatus = true;

  const updateStatus = (status) => {
    const details = status.closest('[data-filter]');
    if (!details) return;

    const selected = Array.from(details.querySelectorAll('[data-facet-swatch-label]'))
      .filter((swatch) => swatch.querySelector('input:checked'))
      .map((swatch) => swatch.dataset.facetSwatchLabel)
      .filter(Boolean);

    status.textContent = selected.length ? `Selected: ${selected.join(', ')}` : 'Choose a finish';
    status.classList.toggle('is-selected', selected.length > 0);
  };

  const updateAllStatuses = (root = document) => {
    root.querySelectorAll('[data-facet-swatch-status]').forEach(updateStatus);
  };

  document.addEventListener('change', (event) => {
    const swatch = event.target.closest?.('[data-facet-swatch-label]');
    const status = swatch?.closest('[data-filter]')?.querySelector('[data-facet-swatch-status]');
    if (status) updateStatus(status);
  });
  document.addEventListener('collection:reloaded', () => updateAllStatuses());
  document.addEventListener('shopify:section:load', (event) => updateAllStatuses(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => updateAllStatuses(), { once: true });
  }
  else {
    updateAllStatuses();
  }
})();

window.addEventListener('pagehide', () => collectionGridHistory.rememberScroll());

if (!customElements.get('facet-form')) {
  customElements.define(
    'facet-form',
    class FacetForm extends HTMLFormElement {
      constructor() {
        super();

        this.dirty = false;
        this.cachedMap = new Map();
        this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
        this.motionReduced = theme.config.motionReduced || this.hasAttribute('motion-reduced');

        this.addEventListener('change', this.onFormChange);
        this.addEventListener('submit', this.onFormSubmit);
      }

      getAnimationParams() {
        return {
          distance: this.isMobile ? 3 : 5,
          duration: this.motionReduced ? 0.001 : 0.18
        };
      }

      onFormChange() {
        this.dirty = true;
        this.dispatchEvent(new Event('submit', { cancelable: true }));
      }

      onFormSubmit(event) {
        event.preventDefault();
        if (!this.dirty) return;

        const url = this.buildUrl().toString();
        this.renderSection(url, event);
      }

      buildUrl() {
        const searchParams = new URLSearchParams(new FormData(this));
        const url = new URL(this.action);

        url.search = '';
        searchParams.forEach((value, key) => url.searchParams.append(key, value));

        ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
          if (url.searchParams.get(item) === '') {
            url.searchParams.delete(item);
          }
        });

        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      updateURLHash(url) {
        const clonedUrl = new URL(url);
        clonedUrl.searchParams.delete('section_id');
        history.replaceState({}, '', clonedUrl.toString());
      }

      beforeRenderSection() {
        const container = document.getElementById('ProductGridContainer');
        if (!container) return;

        container.setAttribute('aria-busy', 'true');
        Motion.animate(container, { opacity: [1, 0.78] }, {
          duration: this.motionReduced ? 0.001 : 0.12,
          easing: 'linear'
        });

        const drawer = document.getElementById('FacetDrawer');
        if (drawer) drawer.classList.add('loading');

        let headerHeight = 0;
        if (!theme.config.mqlSmall && document.querySelector('header.header')) {
          headerHeight = Math.round(document.querySelector('header.header').clientHeight);
        }
        setTimeout(() => {
          const target = document.querySelector('.collection');
          window.scrollTo({
            top: target.getBoundingClientRect().top + window.scrollY - (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20) - headerHeight,
            behavior: theme.config.motionReduced ? 'auto' : 'smooth'
          });
        }, 60);
      }

      afterRenderSection() {
        const container = document.getElementById('ProductGridContainer');
        if (!container) return;

        const { distance, duration } = this.getAnimationParams();
        Motion.animate(container, { y: [distance, 0], opacity: [0.82, 1] }, {
          duration,
          easing: [0.22, 1, 0.36, 1]
        });
        container.removeAttribute('aria-busy');

        const drawer = document.getElementById('FacetDrawer');
        if (drawer) drawer.classList.remove('loading');

        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      }

      renderSection(url, event) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, event)
          : this.renderSectionFromFetch(url, event);

        if (this.hasAttribute('data-history')) this.updateURLHash(url);

        this.dirty = false;
      }

      renderSectionFromFetch(url, event) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.beforeRenderSection();

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = parseCollectionHTML(responseText);
            this.renderFilters(parsedHTML, event);
            this.renderFiltersActive(parsedHTML);
            this.renderProductGridContainer(parsedHTML);
            this.renderProductCount(parsedHTML);
            this.renderSortBy(parsedHTML);
            this.renderCollectionNavigation(parsedHTML, event);

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText, parsedHTML });
            this.cachedMap.set(url, responseText);

            this.afterRenderSection();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSectionFromCache(url, event) {
        this.beforeRenderSection();

        const responseText = this.cachedMap.get(url);
        const parsedHTML = parseCollectionHTML(responseText);
        this.renderFilters(parsedHTML, event);
        this.renderFiltersActive(parsedHTML);
        this.renderProductGridContainer(parsedHTML);
        this.renderProductCount(parsedHTML);
        this.renderSortBy(parsedHTML);
        this.renderCollectionNavigation(parsedHTML, event);

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText, parsedHTML });

        this.afterRenderSection();
      }

      renderFilters(source, event) {
        const parsedHTML = parseCollectionHTML(source);
        const facetElements = parsedHTML.querySelectorAll('#FacetFiltersContainer [data-filter]');

        const matchesIndex = (element) => {
          const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
          return jsFilter ? element.getAttribute('data-index') === jsFilter.getAttribute('data-index') : false;
        };
        const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

        facetsToRender.forEach((element) => {
          const filter = document.querySelector(`[data-filter][data-index="${element.getAttribute('data-index')}"]`);
          if (filter !== null) {
            if (filter.tagName === 'DETAILS') {
              filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
            }
            else {
              filter.innerHTML = element.innerHTML;
            }
          }
        });
      }

      renderFiltersActive(responseText) {
        this._updateSection(responseText, 'FacetFiltersActive');
      }

      renderProductGridContainer(responseText) {
        this._updateSection(responseText, 'ProductGridContainer', (newContent) => {
          newContent.querySelector('motion-list')?.setAttribute('motion-reduced', '');
        });
      }

      renderProductCount(responseText) {
        this._updateSection(responseText, 'ProductCount');
      }

      renderCollectionNavigation(source, event) {
        const host = document.getElementById('WillowCollectionNavigation');
        if (!host) return;

        const activeElement = document.activeElement;
        const clickedChoice = event?.target?.closest?.('[data-wv-choice]');
        const focusedChoice = host.contains(activeElement)
          ? activeElement.closest('[data-wv-choice]')
          : activeElement === document.body && host.contains(clickedChoice) ? clickedChoice : null;
        const oldChoices = Array.from(host.querySelectorAll('[data-wv-choice]'));
        const focusedIndex = oldChoices.indexOf(focusedChoice);
        const scrollPositions = new Map(Array.from(host.querySelectorAll('[data-wv-group]'), (group) => [
          group.dataset.wvGroup, group.querySelector('.wv-collection-nav__links')?.scrollLeft || 0
        ]));

        if (!this._updateSection(source, 'WillowCollectionNavigation')) return;

        const topbar = host.closest('facets-topbar');
        topbar?.classList.toggle('collection__topbar--navigation', Boolean(host.querySelector('.wv-collection-nav')));
        host.querySelectorAll('[data-wv-group]').forEach((group) => {
          const links = group.querySelector('.wv-collection-nav__links');
          if (links) links.scrollLeft = scrollPositions.get(group.dataset.wvGroup) || 0;
        });

        if (!focusedChoice) return;

        // Keep keyboard/touch focus through an AJAX swap without stealing it
        // when the shopper has already moved to another control.
        const isFocusable = (element) => Boolean(element
          && !element.closest('[hidden], [inert], [aria-hidden="true"]')
          && element.getClientRects().length
          && getComputedStyle(element).visibility !== 'hidden');
        const choices = Array.from(host.querySelectorAll('a[data-wv-choice]')).filter(isFocusable);
        const matchingChoice = choices.find((choice) => choice.dataset.wvChoice === focusedChoice.dataset.wvChoice);
        const nextChoice = matchingChoice || choices[Math.min(Math.max(focusedIndex, 0), choices.length - 1)];
        const collection = host.closest('.collection');
        const fallback = nextChoice || Array.from(collection?.querySelectorAll(
          'facets-topbar button:not([disabled]), facets-topbar a[href], facet-sticky button:not([disabled]), #ProductGridContainer a[href]'
        ) || []).find(isFocusable);
        fallback?.focus({ preventScroll: true });
      }

      renderSortBy(responseText) {
        this._updateSection(responseText, 'SortByContainer', (newContent, oldContainer) => {
          const oldFacetSort = oldContainer.querySelector('facet-sort');
          const newFacetSort = newContent.querySelector('facet-sort');
          if (!oldFacetSort || !newFacetSort) return;
          
          if (oldFacetSort.hasAttribute('style')) {
            newFacetSort.setAttribute('style', oldFacetSort.getAttribute('style'));
          }
        });
      }

      _updateSection(source, sectionId, modifier = null) {
        const container = document.getElementById(sectionId);
        if (!container) return false;

        const parsedHTML = parseCollectionHTML(source);
        const newContent = parsedHTML.getElementById(sectionId);
        if (!newContent) return false;

        // Apply custom modifications if provided
        modifier?.(newContent, container);

        container.innerHTML = newContent.innerHTML;
        return true;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('facet-count')) {
  customElements.define(
    'facet-count',
    class FacetCount extends HTMLElement {
      constructor() {
        super();
      }

      facetUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      get itemCount() {
        return parseInt(this.innerText);
      }

      onFacetUpdate(event) {
        const parsedHTML = parseCollectionHTML(event.parsedHTML || event.responseText);
        const facetCount = parsedHTML.querySelector('facet-count');
        this.innerText = facetCount.innerHTML;
        this.hidden = this.itemCount === 0;
      }
    }
  );
}

if (!customElements.get('results-count')) {
  customElements.define(
    'results-count',
    class ResultsCount extends HTMLElement {
      constructor() {
        super();
      }

      facetUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      onFacetUpdate(event) {
        const parsedHTML = parseCollectionHTML(event.parsedHTML || event.responseText);
        const resultsCount = parsedHTML.querySelector('results-count');
        this.innerText = resultsCount.innerHTML;
      }
    }
  );
}

if (!customElements.get('facet-remove')) {
  customElements.define(
    'facet-remove',
    class FacetRemove extends MagnetLink {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          event.preventDefault();

          const url = new URL(this.href);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          form.renderSection(url.toString(), event);
        }
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('facet-sort')) {
  customElements.define(
    'facet-sort',
    class FacetSort extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

        this.addEventListener('change', this.onChange);
        this.button.addEventListener('click', this.show.bind(this));
        this.close.addEventListener('click', this.hide.bind(this));
        document.addEventListener('click', this.onWindowClick.bind(this));
      }

      get listbox() {
        return this.querySelector('.sort-listbox');
      }

      get selection() {
        return this.querySelector('.sort-selection');
      }

      get button() {
        return this.querySelector('.sort-by');
      }

      get close() {
        return this.querySelector('.sort-close');
      }

      init() {
        this.initButton();
        this.style.setProperty('--facet-listbox-height', `${this.listbox.getBoundingClientRect().height.toFixed(1)}px`);
      }

      initButton() {
        const value = this.selection.innerText;
        const width = theme.getElementWidth(this.selection, value);
        this.style.setProperty('--facet-button-width', `${width}px`);
      }

      onChange(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', event.target.value);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          url.searchParams.delete('page');
          form.renderSection(url.toString(), event);
        }

        this.selection.innerText = event.target.nextElementSibling.innerText;
        this.initButton();
        this.hide();
      }

      show() {
        this.button.setAttribute('open', '');
      }

      hide(immediate = true) {
        if (this.button.hasAttribute('open')) {
          setTimeout(() => {
            this.button.removeAttribute('open');
          }, 100);

          if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;
          
          const btnFill = this.button.querySelector('[data-fill');
          Motion.animate(btnFill, { y: ['0%', immediate ? '0%' : '-76%'] }, { duration: 0.6, delay: immediate ? 0 : 0.2 });
        }
      }

      onWindowClick(event) {
        if (!this.contains(event.target)) {
          if (this.button.hasAttribute('open')) {
            this.hide(false);
          }
        }
      }
    }
  );
}

if (!customElements.get('facet-sticky')) {
  customElements.define(
    'facet-sticky',
    class FacetSticky extends HTMLElement {
      constructor() {
        super();

        this.handleIntersection = this.handleIntersection.bind(this);
        this.scheduleObserverRefresh = this.scheduleObserverRefresh.bind(this);
        this.mobileVisible = null;
        this.refreshFrame = 0;
      }

      connectedCallback() {
        document.addEventListener('matchSmall', this.scheduleObserverRefresh);
        document.addEventListener('unmatchSmall', this.scheduleObserverRefresh);
        window.addEventListener('resize', this.scheduleObserverRefresh, { passive: true });
        window.visualViewport?.addEventListener('resize', this.scheduleObserverRefresh, { passive: true });
        this.startObserver();
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        document.removeEventListener('matchSmall', this.scheduleObserverRefresh);
        document.removeEventListener('unmatchSmall', this.scheduleObserverRefresh);
        window.removeEventListener('resize', this.scheduleObserverRefresh);
        window.visualViewport?.removeEventListener('resize', this.scheduleObserverRefresh);
        if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = 0;
      }

      get button() {
        return this.querySelector('.button');
      }

      scheduleObserverRefresh() {
        if (this.refreshFrame) return;
        this.refreshFrame = requestAnimationFrame(() => {
          this.refreshFrame = 0;
          this.startObserver();
        });
      }

      startObserver() {
        this.intersectionObserver?.disconnect();
        this.isMobile = window.matchMedia(theme.config.mediaQuerySmall).matches;
        this.boundary = this.closest('.collection-section')
          || document.querySelector('.collection-section')
          || document.getElementById('ProductGridContainer');
        if (!this.boundary) return;

        let rootMargin;
        if (this.isMobile) {
          const fallbackTop = Math.max(0, window.innerHeight - 160);
          const measuredTop = this.button?.getBoundingClientRect().top;
          const buttonTop = Math.max(0, Math.min(
            window.innerHeight - 1,
            Math.round(measuredTop > 0 ? measuredTop : fallbackTop)
          ));

          // Observe the real CSS viewport strip occupied by the floating pill. This remains
          // stable in Safari and Chrome device mode, where the physical screen height is unrelated.
          rootMargin = `-${buttonTop}px 0px 0px 0px`;
        }
        else {
          const viewportHeight = Math.max(1, window.innerHeight);
          rootMargin = `-${Math.max(0, viewportHeight - 200)}px 0px ${viewportHeight}px 0px`;
          this.mobileVisible = null;
          this.removeAttribute('data-wv-filter-visible');
        }

        this.intersectionObserver = new IntersectionObserver(this.handleIntersection, { rootMargin });
        this.intersectionObserver.observe(this.boundary);
      }

      handleIntersection(entries) {
        const visible = Boolean(entries[0]?.isIntersecting);

        if (this.isMobile) {
          if (this.mobileVisible === visible) return;
          this.mobileVisible = visible;
          this.toggleAttribute('data-wv-filter-visible', visible);
          Motion.animate(this.button, {
            opacity: visible ? 1 : 0,
            visibility: visible ? 'visible' : 'hidden'
          }, { duration: 0.2 });
          return;
        }

        if (visible) {
          Motion.animate(this.button, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
        }
        else {
          Motion.animate(this.button, { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
        }
      }
    }
  );
}

if (!customElements.get('price-range')) {
  customElements.define(
    'price-range',
    class PriceRange extends HTMLElement {
      constructor() {
        super();

        this.rangeMin = this.querySelector('input[type="range"]:first-child');
        this.rangeMax = this.querySelector('input[type="range"]:last-child');
        this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
        this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');

        this.inputMin.addEventListener('focus', this.inputMin.select);
        this.inputMax.addEventListener('focus', this.inputMax.select);
        this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
        this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));

        this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
        this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
        this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
        this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
      }

      onInputMinChange(event) {
        event.preventDefault();
        const target = event.target;
        target.value = Math.max(Math.min(parseInt(target.value || target.min), parseInt(this.inputMax.value || target.max) - 1), target.min);
        this.rangeMin.value = target.value;
        this.rangeMin.parentElement.style.setProperty('--range-min', `${parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max) * 100}%`);
      }

      onInputMaxChange(event) {
        event.preventDefault();
        const target = event.target;
        target.value = Math.min(Math.max(parseInt(target.value || target.max), parseInt(this.inputMin.value || target.min) + 1), target.max);
        this.rangeMax.value = target.value;
        this.rangeMax.parentElement.style.setProperty('--range-max', `${parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max) * 100}%`);
      }

      onRangeMinChange(event) {
        event.stopPropagation();
        this.inputMin.value = event.target.value;
        this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onRangeMaxChange(event) {
        event.stopPropagation();
        this.inputMax.value = event.target.value;
        this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onRangeMinInput(event) {
        event.target.value = Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1);
        event.target.parentElement.style.setProperty('--range-min', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.inputMin.value = event.target.value;
      }

      onRangeMaxInput(event) {
        event.target.value = Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1);
        event.target.parentElement.style.setProperty('--range-max', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.inputMax.value = event.target.value;
      }
    }
  );
}

if (!customElements.get('infinite-button')) {
  customElements.define(
    'infinite-button',
    class InfiniteButton extends HoverButton {
      constructor() {
        super();

        this.onClickHandler = this.onClick.bind(this);
        this.loadingDelay = null;
        this.autoObserver = null;
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickHandler);

        if (this.getAttribute('mode') === 'infinite') {
          this.setAttribute('data-auto-ready', '');
        }

        this.startAutoLoad();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
        this.autoObserver?.disconnect();
        this.autoObserver = null;
        clearTimeout(this.loadingDelay);

        this.abortController?.abort();
      }

      startAutoLoad() {
        if (
          this.getAttribute('mode') !== 'infinite'
          || collectionGridHistory.restoring
          || this.autoObserver
          || this.hasAttribute('aria-busy')
        ) return;

        this.autoObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          this.autoObserver?.disconnect();
          this.autoObserver = null;
          this.onClick(true);
        }, { rootMargin: '800px 0px 800px 0px' });

        this.autoObserver.observe(this);
      }

      getPage(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        return fetch(url, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Collection load failed: ${response.status}`);
            return response.text();
          });
      }

      async onClick(automatic = false) {
        if (this.hasAttribute('aria-busy')) return;

        const isAutomatic = automatic === true;
        this.autoObserver?.disconnect();
        this.autoObserver = null;

        this.enableLoading(isAutomatic);
        const url = this.buildUrl().toString();
        this.loadingDelay = setTimeout(
          () => this.renderLoadingPlaceholders(),
          isAutomatic ? 350 : 650
        );

        try {
          const responseText = await this.getPage(url);
          const parsedHTML = parseCollectionHTML(responseText);
          clearTimeout(this.loadingDelay);
          this.removeLoadingPlaceholders();
          this.renderProductGridContainer(parsedHTML);
          this.renderPagination(parsedHTML);
          collectionGridHistory.recordPage(url);
        }
        catch (error) {
          if (error.name !== 'AbortError') {
            console.error(error);

            if (isAutomatic && this.isConnected) {
              this.setAttribute('mode', 'click');
              this.removeAttribute('data-auto-ready');
            }
          }
        }
        finally {
          clearTimeout(this.loadingDelay);
          this.removeLoadingPlaceholders();

          if (this.isConnected) this.disableLoading();
        }
      }

      renderPagination(responseText) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer === null) return;

        const parsedHTML = parseCollectionHTML(responseText);
        const destination = productGridContainer.querySelector('.pagination');
        const source = parsedHTML.querySelector('.pagination');

        if (source && destination) {
          destination.innerHTML = source.innerHTML;
        }
        else if (destination) {
          destination.remove();
        }
      }

      renderProductGridContainer(responseText) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer === null) return;

        const parsedHTML = parseCollectionHTML(responseText);
        const destination = productGridContainer.querySelector('motion-list');
        const source = parsedHTML.querySelector('motion-list');

        if (source && destination) {
          const appendedItems = appendCollectionCards(source, destination);

          // Infinite loading is append-only: never replace, reorder, or
          // reanimate cards the customer has already browsed.
          document.dispatchEvent(new CustomEvent('collection:reloaded', {
            detail: { mode: 'append' }
          }));
          destination.reload(appendedItems);
        }
      }

      renderLoadingPlaceholders() {
        const destination = document.querySelector('#ProductGridContainer motion-list');
        if (!destination || destination.querySelector('.collection-loading-card')) return;

        const count = window.matchMedia('(max-width: 767px)').matches ? 2 : 3;
        const fragment = document.createDocumentFragment();

        for (let index = 0; index < count; index += 1) {
          const placeholder = document.createElement('div');
          placeholder.className = 'card collection-loading-card';
          placeholder.setAttribute('aria-hidden', 'true');
          placeholder.innerHTML = `
            <div class="collection-loading-card__media"></div>
            <div class="collection-loading-card__content">
              <span></span>
              <span></span>
            </div>
          `;
          fragment.appendChild(placeholder);
        }

        destination.appendChild(fragment);
      }

      removeLoadingPlaceholders() {
        document.querySelectorAll('#ProductGridContainer .collection-loading-card')
          .forEach((placeholder) => placeholder.remove());
      }

      buildUrl() {
        const url = new URL(this.getAttribute('action'));
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      enableLoading(automatic = false) {
        if (automatic) this.setAttribute('data-auto-loading', '');
        this.classList.add('pointer-events-none');
        this.setAttribute('aria-busy', 'true');
      }

      disableLoading() {
        this.classList.remove('pointer-events-none');
        this.removeAttribute('aria-busy');
        this.removeAttribute('data-auto-loading');
      }
    }, { extends: 'button' }
  );
}

queueMicrotask(() => collectionGridHistory.restore());

if (!customElements.get('facets-topbar')) {
  customElements.define(
    'facets-topbar',
    class FacetsTopbar extends StickyElement {
      constructor() {
        super();
      }

      positionStickySidebar() {
        const bounding = this.getBoundingClientRect();
        if (bounding.top === 0) {
          document.body.classList.add('facets-topbar--active');
        }
        else {
          document.body.classList.remove('facets-topbar--active');
        }
      }
    }
  );
}

if (!customElements.get('model-view')) {
  customElements.define(
    'model-view',
    class ModelView extends MagnetButton {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

        this._open = this.hasAttribute('open');
        this.onClickHandler = this.onClick.bind(this);
        this.textSpans = this.querySelectorAll('.link-text');
      }

      set open(value) {
        if (value !== this._open) {
          this._open = value;
          value ? this.setAttribute('open', '') : this.removeAttribute('open');
        }
      }

      static get observedAttributes() {
        return ['open'];
      }

      get open() {
        return this._open;
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickHandler);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'open') {
          const isActive = newValue === '';
          this.setActiveModel(isActive);
          this.updateAccessibility(isActive);
        }
      }

      init() {
        if (theme.config.hasLocalStorage) {
          const isActive = window.localStorage.getItem(`${theme.settings.themeName}:model-view`) || 'false';
          this.open = isActive === 'true';
        }
      }

      onClick(event) {
        event.preventDefault();
        this.open = !this.open;
      }

      setActiveModel(isActive) {
        const container = document.getElementById('ProductGridContainer');
        container.classList.toggle('lifestyle', isActive);

        container.querySelectorAll('video-media').forEach((videoMedia) => videoMedia.pause());

        if (theme.config.hasLocalStorage) {
          window.localStorage.setItem(`${theme.settings.themeName}:model-view`, isActive);
        }
      }

      updateAccessibility(isActive) {
        if (this.textSpans.length === 2) {
          const [defaultText, activeText] = this.textSpans;
          this.setAttribute('aria-label', isActive ? activeText.textContent.trim() : defaultText.textContent.trim());
        }
      }
    }, { extends: 'button' }
  );
}

if (!customElements.get('sub-collections')) {
  customElements.define(
    'sub-collections',
    class SubCollections extends HTMLSelectElement {
      constructor() {
        super();
        this.addEventListener('change', this.onChange);
      }
    
      onChange() {
        window.location.href = this.value;
      }
    }, { extends: 'select' }
  );
}


/* Willow mobile collection grid switch.
   The choice lives across collection pages and is re-applied after facet AJAX / Show more. */
(() => {
  if (window.__wvCollectionGridSwitch) return;
  window.__wvCollectionGridSwitch = true;

  const storageKey = 'willow:collection-grid';
  const readMode = () => {
    try {
      return localStorage.getItem(storageKey) === '2' ? '2' : '1';
    }
    catch (_) {
      return '1';
    }
  };

  const applyMode = (mode, persist = false) => {
    const normalized = mode === '1' ? '1' : '2';
    document.querySelectorAll('#ProductGridContainer .card-grid').forEach((grid) => {
      grid.dataset.wvMobileGrid = normalized;
    });
    document.querySelectorAll('.wv-grid-switch__button').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.wvGrid === normalized));
    });
    if (persist) {
      try {
        localStorage.setItem(storageKey, normalized);
      }
      catch (_) {}
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.wv-grid-switch__button[data-wv-grid]');
    if (!button) return;
    event.preventDefault();
    applyMode(button.dataset.wvGrid, true);
  });

  document.addEventListener('collection:reloaded', () => applyMode(readMode()));
  document.addEventListener('shopify:section:load', () => applyMode(readMode()));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyMode(readMode()), { once: true });
  }
  else {
    applyMode(readMode());
  }
})();

/* Willow desktop collection grid switch.
   It is independent from the mobile 1 / 2 preference and survives facet AJAX / Show more. */
(() => {
  if (window.__wvDesktopCollectionGridSwitch) return;
  window.__wvDesktopCollectionGridSwitch = true;

  const storageKey = 'willow:collection-grid:desktop';
  const legacyStorageKey = 'wv-collection-cols';

  const readMode = () => {
    try {
      const saved = localStorage.getItem(storageKey) || localStorage.getItem(legacyStorageKey);
      if (saved === '3' || saved === '4') return saved;
    }
    catch (_) {}

    const grid = document.querySelector('#ProductGridContainer .card-grid');
    return grid?.dataset.wvDesktopGrid === '4' ? '4' : '3';
  };

  const applyMode = (mode, persist = false) => {
    const normalized = mode === '4' ? '4' : '3';

    document.querySelectorAll('#ProductGridContainer .card-grid').forEach((grid) => {
      Array.from(grid.classList).forEach((className) => {
        if (/^card-grid--\d+$/.test(className)) grid.classList.remove(className);
      });
      grid.classList.add(`card-grid--${normalized}`);
      grid.dataset.wvDesktopGrid = normalized;
    });

    document.querySelectorAll('.wv-desktop-grid-switch__button[data-wv-desktop-grid]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.wvDesktopGrid === normalized));
    });

    if (persist) {
      try {
        localStorage.setItem(storageKey, normalized);
      }
      catch (_) {}
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.wv-desktop-grid-switch__button[data-wv-desktop-grid]');
    if (!button) return;
    event.preventDefault();
    applyMode(button.dataset.wvDesktopGrid, true);
  });

  document.addEventListener('collection:reloaded', () => applyMode(readMode()));
  document.addEventListener('shopify:section:load', () => applyMode(readMode()));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyMode(readMode()), { once: true });
  }
  else {
    applyMode(readMode());
  }
})();
